// RoomContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from "react";
import {
  useRooms,
  useRoom,
  useCreateRoom,
  useUpdateRoom,
  useDeleteRoom,
} from "@/hooks/useRooms";
import { RoomFilters, RoomStatus } from "@/types/room";

// Sorting
type SortField = "number" | "type" | "status" | "floor";
type SortDirection = "asc" | "desc" | null;

interface RoomContextType {
  // Query Hooks
  useRooms: (
    hotelId: string,
    page?: number,
    limit?: number,
    filters?: RoomFilters
  ) => ReturnType<typeof useRooms>;
  useRoom: (id: string, hotelId: string) => ReturnType<typeof useRoom>;

  // Mutation Hooks
  useCreateRoom: () => ReturnType<typeof useCreateRoom>;
  useUpdateRoom: () => ReturnType<typeof useUpdateRoom>;
  useDeleteRoom: () => ReturnType<typeof useDeleteRoom>;

  // Filters
  filters: {
    status: string;
    category: string;
    currentPage: number;
    sortField: SortField | null;
    sortDirection: SortDirection;
    selectedRooms: string[];
  };

  // Actions
  actions: {
    setStatus: (value: string) => void;
    setCategory: (value: string) => void;
    setCurrentPage: (page: number) => void;
    setSortField: (field: SortField | null) => void;
    setSortDirection: (dir: SortDirection) => void;
    setSelectedRooms: (ids: string[]) => void;
    handleSort: (field: string) => void;
    handleFilterChange: (
      setter: (value: string) => void,
      value: string
    ) => void;
    handleSelectRoom: (id: string, checked: boolean) => void;
    handleSelectAll: (checked: boolean, roomIds: string[]) => void;
    resetFilters: () => void;
  };

  // Computed
  computed: {
    apiFilters: RoomFilters;
    limit: number;
  };
}

const RoomContext = createContext<RoomContextType | undefined>(undefined);

interface RoomProviderProps {
  children: ReactNode;
}

export const RoomProvider: React.FC<RoomProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<string>("all statuses");
  const [category, setCategory] = useState<string>("all categories");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const limit = 10;

const handleSort = (field: string) => {
  const newSortField = field as SortField;

  if (sortField === newSortField) {
    let nextDirection: SortDirection;

    if (sortDirection === "asc") {
      nextDirection = "desc";
    } else if (sortDirection === "desc") {
      nextDirection = null;
    } else {
      nextDirection = "asc";
    }

    setSortDirection(nextDirection);

    if (nextDirection === null) {
      setSortField(null);
    }
  } else {
    setSortField(newSortField);
    setSortDirection("asc");
  }

  setCurrentPage(1);
};

  const handleFilterChange = (
    setter: (value: string) => void,
    value: string
  ) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSelectRoom = (id: string, checked: boolean) => {
    setSelectedRooms((prev) =>
      checked ? [...prev, id] : prev.filter((r) => r !== id)
    );
  };

  const handleSelectAll = (checked: boolean, roomIds: string[]) => {
    if (checked) setSelectedRooms(roomIds);
    else setSelectedRooms([]);
  };

  const resetFilters = () => {
    setStatus("all statuses");
    setCategory("all categories");
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
    setSelectedRooms([]);
  };

  const apiFilters: RoomFilters = useMemo(() => {
    const filters: RoomFilters = {};
    if (status !== "all statuses") filters.status = status as RoomStatus;
    if (category !== "all categories") filters.categoryId = category;
    if (sortField && sortDirection) {
      filters.sort = `${sortField}:${sortDirection}`;
    }
    filters.page = currentPage;
    filters.limit = limit;
    return filters;
  }, [status, category, sortField, sortDirection, currentPage]);

  const contextValue: RoomContextType = {
    useRooms,
    useRoom,
    useCreateRoom,
    useUpdateRoom,
    useDeleteRoom,
    filters: {
      status,
      category,
      currentPage,
      sortField,
      sortDirection,
      selectedRooms,
    },
    actions: {
      setStatus,
      setCategory,
      setCurrentPage,
      setSortField,
      setSortDirection,
      setSelectedRooms,
      handleSort,
      handleFilterChange,
      handleSelectRoom,
      handleSelectAll,
      resetFilters,
    },
    computed: {
      apiFilters,
      limit,
    },
  };

  return (
    <RoomContext.Provider value={contextValue}>{children}</RoomContext.Provider>
  );
};

export const useRoomContext = () => {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error("useRoomContext must be used within a RoomProvider");
  }
  return context;
};
